/**
 * Lot History modal — look up any ID across all tables, render its summary,
 * and link from there to any related lot/sale/trade/grading.
 *
 * Mounts into #pickerRoot so it stacks above edit-form modals.
 */
import { DB } from "../lib/db";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmtDate, fmtN } from "../utils/format";
import { currentPullFMVForRip, realizedFMVForRip, pullBasisFMVForRip } from "../lib/derived";
import { isBulkPoolRow, bulkUnitValueFor, type BulkClass } from "../lib/bulk";
import type { Row, TableKey } from "../lib/types";

type EventKind = "sealed" | "card" | "rip" | "sale" | "trade" | "grading" | "adjustment" | "purchase" | "bulk-pool";
interface ResolvedEvent { kind: EventKind; row: Row; }

function resolveLotEvent(id: string): ResolvedEvent | null {
  const sid = String(id).trim();
  if (!sid) return null;
  // Bulk pools live in the cards table but use a dedicated renderer that scans
  // every event source, since pool basis comes from Settings and not Source ID.
  const poolRow = DB.table("cards").find(r => r["Card Lot ID"] === sid && isBulkPoolRow(r));
  if (poolRow) return { kind: "bulk-pool", row: poolRow };
  const checks: Array<[TableKey, string, EventKind]> = [
    ["sealed",       "Lot ID",        "sealed"],
    ["cards",        "Card Lot ID",   "card"],
    ["rips",         "Rip ID",        "rip"],
    ["sales",        "Sale ID",       "sale"],
    ["trades",       "Trade ID",      "trade"],
    ["grading",      "Submission ID", "grading"],
    ["transactions", "Txn ID",        "adjustment"],
    ["purchases",    "Purchase ID",   "purchase"],
  ];
  for (const [t, field, kind] of checks) {
    const row = DB.table(t).find(r => r[field] === sid);
    if (row) return { kind, row };
  }
  return null;
}

/** Map a pool Lot ID back to its display class. */
function poolIdToClass(id: string | undefined): BulkClass | null {
  if (id === "BULK")       return "Bulk";
  if (id === "HOLO-BULK")  return "Holo Bulk";
  if (id === "PREM-BULK")  return "Premium Bulk";
  if (id === "BULK-1-5")   return "$1-$5 Bulk";
  if (id === "BULK-5-10")  return "$5-$10 Bulk";
  return null;
}

/* ------------------------------------------------------------------ */
/*  Tiny rendering helpers                                              */
/* ------------------------------------------------------------------ */
function idChip(id: string | undefined): string {
  if (!id) return `<span class="text-[var(--muted)]">—</span>`;
  const safe = escapeHtml(id);
  return `<a class="font-mono text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${safe}')">${safe}</a>`;
}
function money(v: any): string { return (v == null || v === "") ? "—" : fmt$(v); }
function section(title: string, body: string): string {
  return `<div class="panel-2 p-3 mb-3"><div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">${title}</div>${body}</div>`;
}

/** Helper: find any trade whose Outgoing or Incoming array references this lot. */
function findTradesUsing(lotId: string): Row[] {
  return DB.table("trades").filter(t => {
    if (String(t["Status"] || "Accepted") !== "Accepted") return false;
    const out: any[] = Array.isArray(t.Outgoing) ? t.Outgoing : [];
    const inc: any[] = Array.isArray(t.Incoming) ? t.Incoming : [];
    return out.some(x => x["Lot ID"] === lotId) || inc.some(x => x["Lot ID"] === lotId);
  });
}

/* ------------------------------------------------------------------ */
/*  Per-entity history renderers                                        */
/* ------------------------------------------------------------------ */
function renderPurchase(r: Row): string {
  const items: any[] = Array.isArray(r.Items) ? r.Items : [];
  const summary = `
    <div class="font-semibold text-base">Purchase</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Date"]))} · ${escapeHtml(r["Vendor"] || "(no vendor)")} · ${escapeHtml(r["Payment Method"] || "")}</div>
    <div class="text-xs text-[var(--muted)] mt-1">Subtotal ${money(r["Subtotal"])} + Fees ${money(r["Fees"])} = Total Paid <b>${money(r["Total Paid"])}</b></div>`;
  const itemRows = items.length
    ? items.map(it => `<div>• ${it["Lot ID"] ? idChip(it["Lot ID"]) : "<i>no lot</i>"} · <b>${escapeHtml(it.Type || "")}</b> · ${escapeHtml(it.Item || "")}${it.Qty > 1 ? ` ×${it.Qty}` : ""} @ ${money(it.UnitCost)}</div>`).join("")
    : `<div class="text-[var(--muted)] text-sm">No items.</div>`;
  return `
    ${section("Summary", summary)}
    ${section("Items Purchased", itemRows)}
    ${r["Notes"] ? section("Notes", `<div class="text-sm">${escapeHtml(r["Notes"])}</div>`) : ""}`;
}

function renderSealed(r: Row): string {
  const id = r["Lot ID"];
  const summary = `
    <div class="font-semibold text-base">${escapeHtml(r["Product"] || "(unnamed)")}</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(r["Game"] || "")} · ${escapeHtml(r["Set"] || "")} · ${escapeHtml(r["Product Type"] || "")} · Qty ${fmtN(r["Qty"])}</div>
    <div class="text-xs text-[var(--muted)] mt-1">Acq: ${escapeHtml(fmtDate(r["Acq Date"]))} · ${escapeHtml(r["Acq Type"] || "")} · Total Basis ${money(r["Total Basis"])} · Total FMV ${money(r["Current FMV"])}</div>`;
  const sourceId = r["Source ID"];
  const incoming = sourceId
    ? `<div>↳ Acquired via ${idChip(sourceId)}</div>`
    : `<div class="text-[var(--muted)] text-sm">No linked source record.</div>`;
  const rips   = DB.table("rips").filter(x => x["Sealed Lot ID"] === id);
  const sales  = DB.table("sales").filter(x => Array.isArray(x.Items) ? x.Items.some((it: any) => it["Lot ID"] === id) : x["Lot ID"] === id);
  const trades = findTradesUsing(id);
  const events: string[] = [];
  rips.forEach(rp => {
    const pullIds = (rp["New Card Lot IDs"] || "").split(/[,\s]+/).filter(Boolean);
    events.push(`<div>✂ Ripped on ${escapeHtml(fmtDate(rp["Date"]))} (${rp["Qty Opened"] || 1} unit${(rp["Qty Opened"] || 1) === 1 ? "" : "s"}) → ${idChip(rp["Rip ID"])} · pulls: ${pullIds.length ? pullIds.map(idChip).join(", ") : "—"}</div>`);
  });
  sales.forEach(sl => {
    const item = Array.isArray(sl.Items) ? sl.Items.find((it: any) => it["Lot ID"] === id) : null;
    const detail = item
      ? `${money(item["Sale Price"])} (this item)${item["COGS"] ? ` · COGS ${money(item["COGS"])}` : ""}`
      : `net ${money(sl["Net Profit"])}`;
    events.push(`<div>$ Sold on ${escapeHtml(fmtDate(sl["Date"]))} → ${idChip(sl["Sale ID"])} · ${detail}</div>`);
  });
  trades.forEach(tr => events.push(`<div>⇄ Traded on ${escapeHtml(fmtDate(tr["Date"]))} → ${idChip(tr["Trade ID"])}</div>`));
  // User's own valuation history for THIS lot - drawn from fmv_history table.
  // Always rendered (placeholder if not enough data yet).
  const fmvHistorySection = section("Your FMV history",
    `<div style="height:200px; position:relative">
       <canvas data-lot-fmv-history-chart data-lot-kind="sealed" data-lot-id="${escapeHtml(String(id))}"></canvas>
       <div data-lot-fmv-history-empty class="hidden absolute inset-0 flex items-center justify-center text-[var(--muted)] text-sm text-center px-2">Need at least 2 valuation points to chart a trend. New snapshots are recorded automatically whenever this lot's FMV changes.</div>
     </div>
     <div class="text-xs text-[var(--muted)] mt-1">Per-unit value over time. Snapshots are added every time this lot's Current FMV changes (manual edit, refresh, picker, or nightly sweep).</div>`);
  // Price history chart - only when the lot is linked to a TCGCSV product.
  // The canvas is filled after the modal mounts via wireSealedHistoryChart().
  const pid = Number(r["TCG Product ID"]) || 0;
  const priceHistorySection = pid > 0
    ? section("Market price (90 days)",
        `<div style="height:200px; position:relative">
           <canvas data-sealed-history-chart="${pid}"></canvas>
           <div data-sealed-history-empty class="hidden absolute inset-0 flex items-center justify-center text-[var(--muted)] text-sm">Catalog has no price history for this product yet.</div>
         </div>
         <div class="text-xs text-[var(--muted)] mt-1">Linked to TCGCSV product #${pid}. Updated daily.</div>`)
    : "";
  return `
    ${section("Summary", summary)}
    ${section("Origin", incoming)}
    ${section("Activity", events.length ? events.join("") : `<div class="text-[var(--muted)] text-sm">No rips, sales, or trades recorded against this lot.</div>`)}
    ${fmvHistorySection}
    ${priceHistorySection}`;
}

/** Mount Chart.js into any pending sealed-history canvases. Called after the
 *  modal's innerHTML lands. No-ops if there's no eligible canvas in the DOM. */
async function wireSealedHistoryChart(): Promise<void> {
  const canvases = document.querySelectorAll<HTMLCanvasElement>("canvas[data-sealed-history-chart]");
  if (!canvases.length) return;
  const { renderSealedPriceHistory } = await import("./priceHistoryChart");
  for (const c of canvases) {
    const pid = Number(c.getAttribute("data-sealed-history-chart")) || 0;
    if (!pid) continue;
    const chart = await renderSealedPriceHistory(c, pid, 90);
    if (!chart) {
      // No data — surface the "empty" overlay sibling
      const wrap = c.parentElement;
      const empty = wrap?.querySelector<HTMLElement>("[data-sealed-history-empty]");
      if (empty) empty.classList.remove("hidden");
      c.style.display = "none";
    }
  }
}

/** Mount Chart.js into the per-lot FMV-history canvases (cards + sealed).
 *  Reveals an "empty" overlay when the lot has < 2 snapshots so far. */
async function wireLotFmvHistoryCharts(): Promise<void> {
  const canvases = document.querySelectorAll<HTMLCanvasElement>("canvas[data-lot-fmv-history-chart]");
  if (!canvases.length) return;
  const { renderLotFmvHistory } = await import("./priceHistoryChart");
  for (const c of canvases) {
    const kind = (c.getAttribute("data-lot-kind") || "card") as "card" | "sealed";
    const lotId = c.getAttribute("data-lot-id") || "";
    if (!lotId) continue;
    const chart = await renderLotFmvHistory(c, kind, lotId);
    if (!chart) {
      const wrap = c.parentElement;
      const empty = wrap?.querySelector<HTMLElement>("[data-lot-fmv-history-empty]");
      if (empty) empty.classList.remove("hidden");
      c.style.display = "none";
    }
  }
}

function renderCard(r: Row): string {
  const id = r["Card Lot ID"];
  const summary = `
    <div class="font-semibold text-base">${escapeHtml(r["Card Name"] || "(unnamed)")}</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(r["Game"] || "")} · ${escapeHtml(r["Set"] || "")} #${escapeHtml(r["Card #"] || "")} · ${escapeHtml(r["Condition/Grade"] || "")} · Qty ${fmtN(r["Qty"])}</div>
    <div class="text-xs text-[var(--muted)] mt-1">Acq: ${escapeHtml(fmtDate(r["Acq Date"]))} · ${escapeHtml(r["Acq Type"] || "")} · Total Basis ${money(r["Total Basis"])} · Total FMV ${money(r["Current FMV"])} · Status ${escapeHtml(r["Status"] || "")}</div>`;
  const sourceId = r["Source ID"];
  const incoming = sourceId
    ? `<div>↳ Came from ${idChip(sourceId)}</div>`
    : `<div class="text-[var(--muted)] text-sm">No linked source record.</div>`;
  const grading = DB.table("grading").filter(x => Array.isArray(x.Cards) && x.Cards.some((c: any) => c["Card Lot ID"] === id));
  const sales   = DB.table("sales").filter(x => Array.isArray(x.Items) ? x.Items.some((it: any) => it["Lot ID"] === id) : x["Lot ID"] === id);
  const trades  = findTradesUsing(id);
  const events: string[] = [];
  grading.forEach(g => {
    const c = (g.Cards || []).find((x: any) => x["Card Lot ID"] === id) || {};
    const services = Array.isArray(g["Service Types"]) ? g["Service Types"].join(" + ") : "Grading";
    events.push(`<div>★ ${escapeHtml(services)} on ${escapeHtml(fmtDate(g["Date Sent"]))} → ${idChip(g["Submission ID"])} · grade: ${escapeHtml(c["Grade"] || "pending")}${c["Post-FMV"] ? ` · post-FMV ${money(c["Post-FMV"])}` : ""}</div>`);
  });
  sales.forEach(sl => {
    const item = Array.isArray(sl.Items) ? sl.Items.find((it: any) => it["Lot ID"] === id) : null;
    const detail = item
      ? `${money(item["Sale Price"])} (this item)${item["COGS"] ? ` · COGS ${money(item["COGS"])}` : ""}`
      : `net ${money(sl["Net Profit"])}`;
    events.push(`<div>$ Sold on ${escapeHtml(fmtDate(sl["Date"]))} → ${idChip(sl["Sale ID"])} · ${detail}</div>`);
  });
  trades.forEach(tr => events.push(`<div>⇄ Traded on ${escapeHtml(fmtDate(tr["Date"]))} → ${idChip(tr["Trade ID"])}</div>`));
  return `
    ${section("Summary", summary)}
    ${section("Origin", incoming)}
    ${section("Activity", events.length ? events.join("") : `<div class="text-[var(--muted)] text-sm">No grading, sales, or trades recorded against this lot.</div>`)}
    ${section("Your FMV history",
      `<div style="height:200px; position:relative">
         <canvas data-lot-fmv-history-chart data-lot-kind="card" data-lot-id="${escapeHtml(String(id))}"></canvas>
         <div data-lot-fmv-history-empty class="hidden absolute inset-0 flex items-center justify-center text-[var(--muted)] text-sm text-center px-2">Need at least 2 valuation points to chart a trend. New snapshots are recorded automatically whenever this lot's FMV changes.</div>
       </div>
       <div class="text-xs text-[var(--muted)] mt-1">Per-unit value over time. Snapshots are added every time this lot's Current FMV changes (manual edit, refresh, picker, or nightly sweep). Graded slabs are still valued manually.</div>`)}`;
}

function renderRip(r: Row): string {
  const ripId = r["Rip ID"];
  const sealedId = r["Sealed Lot ID"];
  const newIds: string[] = (r["New Card Lot IDs"] || "").split(/[,\s]+/).filter(Boolean);
  const basisRemoved = Number(r["Basis Removed"]) || 0;
  const initialFMV  = pullBasisFMVForRip(r);
  const currentFMV  = currentPullFMVForRip(ripId);
  const realized    = realizedFMVForRip(ripId);
  const currentEV   = +(currentFMV - basisRemoved).toFixed(2);
  const delta       = +(currentFMV - initialFMV).toFixed(2);
  const deltaColor  = delta >= 0 ? "text-emerald-300" : "text-rose-300";

  const summary = `
    <div class="font-semibold text-base">Box Rip / Break</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Date"]))} · ${r["Qty Opened"] || 1} unit(s) of ${escapeHtml(r["Sealed Product"] || "")}</div>
    <div class="text-xs text-[var(--muted)] mt-1">Basis Removed ${money(basisRemoved)}</div>`;

  const fmvTable = `
    <div class="grid grid-cols-2 gap-2 text-sm">
      <div>Pull FMV (at rip)</div>     <div class="text-right stat-num text-[var(--muted)]">${money(initialFMV)}</div>
      <div>Current FMV (live)</div>    <div class="text-right stat-num">${money(currentFMV)} ${delta !== 0 ? `<span class="${deltaColor} text-xs">(${delta >= 0 ? "+" : "−"}${fmt$(Math.abs(delta))})</span>` : ""}</div>
      ${realized > 0 ? `<div>Already sold from this rip</div><div class="text-right stat-num text-emerald-300">${money(realized)}</div>` : ""}
      <div class="pt-1 border-t border-[var(--border)] mt-1">EV (current FMV − basis)</div>
      <div class="pt-1 border-t border-[var(--border)] mt-1 text-right stat-num ${currentEV >= 0 ? 'text-emerald-300' : 'text-rose-300'}"><b>${money(currentEV)}</b></div>
    </div>`;

  return `
    ${section("Summary", summary)}
    ${section("Origin", `<div>↳ Opened sealed lot ${idChip(sealedId)}</div>`)}
    ${section("Value (tracks pulled card lots)", fmvTable)}
    ${section("Cards Pulled", newIds.length ? newIds.map(idChip).join("<br>") : `<div class="text-[var(--muted)] text-sm">No card lots recorded.</div>`)}`;
}

function renderSale(r: Row): string {
  const items: any[] = Array.isArray(r.Items) ? r.Items : [];
  const subtotal = items.reduce((s, x) => s + (Number(x["Sale Price"]) || 0), 0);
  const cogs     = items.reduce((s, x) => s + (Number(x["COGS"])       || 0), 0);
  const totalRealized = items.reduce((s, x) => s + (Number(x["Realized Gain"]) || 0), 0);
  const summary = `
    <div class="font-semibold text-base">Sale</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Date"]))} · ${escapeHtml(r["Platform"] || "")}${r["Buyer"] ? ` · Buyer ${escapeHtml(r["Buyer"])}` : ""}</div>
    <div class="text-xs text-[var(--muted)] mt-1">Subtotal ${money(subtotal || r["Subtotal"] || r["Sales Price"])} − Fees ${money(r["Fees"])} − Ship ${money(r["Shipping"])} − COGS ${money(cogs || r["COGS"])} = <b>Net ${money(r["Net Profit"])}</b></div>
    ${totalRealized !== 0 ? `<div class="text-xs mt-1">Realized gain on items: <b class="${totalRealized >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${money(totalRealized)}</b></div>` : ""}`;
  const fmtSaleItem = (it: any) => {
    const qty = Number(it.Qty) || 1;
    const treatment = it["Tax Treatment"];
    const holdingDays = it["Holding Days"];
    const realized = Number(it["Realized Gain"]) || 0;
    const holdInfo = treatment
      ? ` · ${holdingDays != null ? `${holdingDays}d ` : ""}${treatment === "Long-term" ? `<span class="pill pill-blue">Long-term</span>`
        : treatment === "Short-term" ? `<span class="pill pill-amber">Short-term</span>`
        : `<span class="pill pill-gray" title="Acq Date missing on source lot">Unknown</span>`}`
      : "";
    const gainCls = realized >= 0 ? "text-emerald-300" : "text-rose-300";
    const gainInfo = it["Realized Gain"] != null ? ` · <span class="${gainCls}">Realized ${money(realized)}</span>` : "";
    return `• ${it["Lot ID"] ? idChip(it["Lot ID"]) : "<i>free-text</i>"} · ${escapeHtml(it.Item || "")}${qty > 1 ? ` ×${qty}` : ""} @ ${money(it["Sale Price"])} (COGS ${money(it["COGS"])})${holdInfo}${gainInfo}`;
  };
  const itemRows = items.length
    ? items.map(it => `<div>${fmtSaleItem(it)}</div>`).join("")
    : (r["Lot ID"] ? `<div>↳ ${idChip(r["Lot ID"])} · ${escapeHtml(r["Item"] || "")} · sold for ${money(r["Sales Price"])}</div>` : `<div class="text-[var(--muted)] text-sm">No items.</div>`);
  return `
    ${section("Summary", summary)}
    ${section("Items Sold", itemRows)}
    ${r["Notes"] ? section("Notes", `<div class="text-sm">${escapeHtml(r["Notes"])}</div>`) : ""}`;
}

function renderTrade(r: Row): string {
  const out: any[] = Array.isArray(r.Outgoing) ? r.Outgoing : [];
  const inc: any[] = Array.isArray(r.Incoming) ? r.Incoming : [];
  const realized = r["Realized Gain"] != null
    ? Number(r["Realized Gain"])
    : out.reduce((s, x) => s + ((Number(x.FMV) || 0) - (Number(x.Basis) || 0)) * (Number(x.Qty) || 1), 0);
  const inFMV  = inc.reduce((s, x) => s + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
  const outFMV = out.reduce((s, x) => s + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
  const netGL  = +(inFMV - outFMV + (Number(r["Cash"]) || 0)).toFixed(2);
  const fmtOutItem = (x: any) => {
    const qty = Number(x.Qty) || 1;
    const realizedPer = x["Realized Gain"] != null
      ? Number(x["Realized Gain"])
      : ((Number(x.FMV) || 0) - (Number(x.Basis) || 0)) * qty;
    const treatment = x["Tax Treatment"];
    const holdingDays = x["Holding Days"];
    const holdInfo = treatment
      ? ` · ${holdingDays != null ? `${holdingDays}d ` : ""}${treatment === "Long-term" ? `<span class="pill pill-blue">Long-term</span>`
        : treatment === "Short-term" ? `<span class="pill pill-amber">Short-term</span>`
        : `<span class="pill pill-gray" title="Acq Date missing on source lot">Unknown</span>`}`
      : "";
    const gainCls = realizedPer >= 0 ? "text-emerald-300" : "text-rose-300";
    return `${x["Lot ID"] ? idChip(x["Lot ID"]) : "<i>free text</i>"} · ${escapeHtml(x.Item || "")}${qty > 1 ? ` ×${qty}` : ""} · Basis ${money(x.Basis)} → FMV ${money(x.FMV)}${holdInfo} · <span class="${gainCls}">Realized ${money(realizedPer)}</span>`;
  };
  const fmtInItem = (x: any) => `${x["Lot ID"] ? idChip(x["Lot ID"]) : "<i>free text</i>"} · ${escapeHtml(x.Item || "")}${x.Qty > 1 ? ` ×${x.Qty}` : ""} · FMV ${money(x.FMV)}`;
  const summary = `
    <div class="font-semibold text-base">Trade</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Date"]))} · Out FMV ${money(r["Outgoing FMV"])} · In FMV ${money(r["Incoming FMV"])} · Net G/L ${money(netGL)}</div>
    <div class="text-xs mt-1">Realized gain on outgoing items: <b class="${realized >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${money(realized)}</b></div>`;
  return `
    ${section("Summary", summary)}
    ${section("Outgoing (taxable disposition)", out.length ? out.map(x => `<div>← ${fmtOutItem(x)}</div>`).join("") : `<div class="text-[var(--muted)] text-sm">None.</div>`)}
    ${section("Incoming", inc.length ? inc.map(x => `<div>→ ${fmtInItem(x)}</div>`).join("") : `<div class="text-[var(--muted)] text-sm">None.</div>`)}`;
}

function renderGrading(r: Row): string {
  const cards: any[] = Array.isArray(r.Cards) ? r.Cards : [];
  const services = Array.isArray(r["Service Types"]) ? r["Service Types"].join(" + ") : "Grading";
  const summary = `
    <div class="font-semibold text-base">${escapeHtml(services)} Submission</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Date Sent"]))} · ${escapeHtml(r["Provider"] || "(no provider)")} · Status ${escapeHtml(r["Status"] || "")}${r["Date Returned"] ? ` · returned ${escapeHtml(fmtDate(r["Date Returned"]))}` : ""}</div>
    <div class="text-xs text-[var(--muted)] mt-1">${cards.length} card(s) · Service Fees ${money(r["Total Service Fees"])} + Shipping ${money(r["Total Shipping"])} · Pre-FMV ${money(r["Total Pre-FMV"])} → Post-FMV ${money(r["Total Post-FMV"])}</div>`;
  const list = cards.length
    ? cards.map(c => `<div>• ${c["Card Lot ID"] ? idChip(c["Card Lot ID"]) : "<i>unlinked</i>"} · ${escapeHtml(c["Card Name"] || "")}${c["Set"] ? ` · ${escapeHtml(c["Set"])}` : ""}${c["Number"] ? ` #${escapeHtml(c["Number"])}` : ""} · Fee ${money(c["Service Fee"])} · Pre ${money(c["Pre-FMV"])} → ${c["Grade"] ? `<b>${escapeHtml(c["Grade"])}</b>` : "pending"} ${money(c["Post-FMV"])}</div>`).join("")
    : `<div class="text-[var(--muted)] text-sm">No cards.</div>`;
  return `
    ${section("Summary", summary)}
    ${section("Cards in Submission", list)}
    ${r["Notes"] ? section("Notes", `<div class="text-sm">${escapeHtml(r["Notes"])}</div>`) : ""}`;
}

function renderAdjustment(r: Row): string {
  const isGift = r["Type"] === "Gift";
  const taxCat = r["Tax Category"];
  const summary = `
    <div class="font-semibold text-base">Adjustment · ${escapeHtml(r["Type"] || "")}${isGift && taxCat ? ` <span class="pill pill-amber">${escapeHtml(taxCat)}</span>` : ""}</div>
    <div class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Date"]))} · Cash In ${money(r["Cash In"])} · Cash Out ${money(r["Cash Out"])}${(Number(r["Basis Removed"]) || 0) ? ` · Basis Removed ${money(r["Basis Removed"])}` : ""}</div>
    ${r["Description"] ? `<div class="text-xs mt-1">${escapeHtml(r["Description"])}</div>` : ""}`;

  const items: any[] = Array.isArray(r.Items) ? r.Items : [];
  const itemsHeading = isGift ? "Gifted Items" : "Contributed Items";
  const itemRows = items.length
    ? items.map(it => `<div>• ${it["Lot ID"] ? idChip(it["Lot ID"]) : "<i>no lot</i>"} · <b>${escapeHtml(it.AsType || "")}</b> · ${escapeHtml(it.Item || "")}${(it.Qty || 1) > 1 ? ` ×${it.Qty}` : ""} @ ${money(it.UnitBasis)}${(it.Qty || 1) > 1 ? ` <span class="text-[var(--muted)]">(line ${money((Number(it.UnitBasis) || 0) * (Number(it.Qty) || 1))})</span>` : ""}</div>`).join("")
    : "";

  const linkedSealed = DB.table("sealed").filter(x => x["Source ID"] === r["Txn ID"]);
  const linkedCards  = DB.table("cards").filter(x  => x["Source ID"] === r["Txn ID"]);
  const linked = [...linkedSealed.map(x => x["Lot ID"]), ...linkedCards.map(x => x["Card Lot ID"])];

  return `
    ${section("Summary", summary)}
    ${items.length ? section(itemsHeading, itemRows) : ""}
    ${r["Related Lot ID"] ? section("Related Lot", `<div>↳ ${idChip(r["Related Lot ID"])}</div>`) : ""}
    ${linked.length ? section("Lots Tagged with this Adjustment", linked.map(idChip).join("<br>")) : ""}`;
}

/* ------------------------------------------------------------------ */
/*  Bulk pool history — scans all event sources for activity            */
/* ------------------------------------------------------------------ */
interface BulkActivityEvent {
  date: string;
  source: "Purchase" | "Rip" | "Trade" | "Sale" | "Adjustment";
  sourceId: string;
  delta: number;
  note: string;
}

function collectBulkActivity(poolId: string): BulkActivityEvent[] {
  const events: BulkActivityEvent[] = [];
  DB.table("purchases").forEach(p => {
    const items: any[] = Array.isArray(p.Items) ? p.Items : [];
    items.forEach(it => {
      if (it["Lot ID"] === poolId) {
        events.push({
          date: p["Date"] || "",
          source: "Purchase",
          sourceId: p["Purchase ID"] || "",
          delta: +Number(it.Qty || 0),
          note: `${p["Vendor"] || ""}${it.UnitCost ? ` · @ ${fmt$(it.UnitCost)}/c paid` : ""}`,
        });
      }
    });
  });
  DB.table("rips").forEach(r => {
    const bps: any[] = Array.isArray(r["Bulk Pulls"]) ? r["Bulk Pulls"] : [];
    bps.forEach(bp => {
      if (poolIdToClass(poolId) === bp["Class"]) {
        events.push({
          date: r["Date"] || "",
          source: "Rip",
          sourceId: r["Rip ID"] || "",
          delta: +Number(bp["Qty"] || 0),
          note: `${r["Sealed Product"] || ""} (×${r["Qty Opened"] || 1})`,
        });
      }
    });
  });
  DB.table("trades").forEach(t => {
    if (String(t["Status"] || "Accepted") !== "Accepted") return;
    const out: any[] = Array.isArray(t.Outgoing) ? t.Outgoing : [];
    out.forEach(x => {
      if (x["Lot ID"] === poolId && x.Type === "Bulk") {
        events.push({
          date: t["Date"] || "",
          source: "Trade",
          sourceId: t["Trade ID"] || "",
          delta: -Number(x.Qty || 0),
          note: "outgoing",
        });
      }
    });
    const inc: any[] = Array.isArray(t.Incoming) ? t.Incoming : [];
    inc.forEach(x => {
      if (x["Lot ID"] === poolId && x.AsType === "Bulk") {
        events.push({
          date: t["Date"] || "",
          source: "Trade",
          sourceId: t["Trade ID"] || "",
          delta: +Number(x.Qty || 0),
          note: "incoming",
        });
      }
    });
  });
  DB.table("sales").forEach(s => {
    const items: any[] = Array.isArray(s.Items) ? s.Items : [];
    items.forEach(it => {
      if (it["Lot ID"] === poolId && it.Type === "Bulk") {
        events.push({
          date: s["Date"] || "",
          source: "Sale",
          sourceId: s["Sale ID"] || "",
          delta: -Number(it.Qty || 0),
          note: `${s["Platform"] || ""}${it["Sale Price"] ? ` · sold for ${fmt$(it["Sale Price"])}` : ""}`,
        });
      }
    });
  });
  DB.table("transactions").forEach(a => {
    if (a["Related Lot ID"] === poolId) {
      const isOut = a["Type"] === "Gift" || (Number(a["Basis Removed"]) || 0) > 0;
      events.push({
        date: a["Date"] || "",
        source: "Adjustment",
        sourceId: a["Txn ID"] || "",
        delta: isOut ? -1 : +1,
        note: `${a["Type"] || ""} · ${a["Description"] || ""}`,
      });
    }
  });
  events.sort((a, b) => String(a.date).localeCompare(String(b.date)) || a.source.localeCompare(b.source));
  return events;
}

let _bulkFilters: { poolId: string; sources: Set<string>; from: string; to: string } = {
  poolId: "", sources: new Set(), from: "", to: "",
};

const ALL_BULK_SOURCES = ["Purchase", "Rip", "Trade", "Sale", "Adjustment"];

function srcPillColor(s: string): string {
  return s === "Purchase" ? "blue"
       : s === "Rip"      ? "violet"
       : s === "Trade"    ? "cyan"
       : s === "Sale"     ? "emerald"
       :                    "gray";
}

function bulkActivityTableHtml(events: BulkActivityEvent[], poolQty: number, currentFilterActive: boolean): string {
  if (!events.length) {
    return currentFilterActive
      ? `<div class="text-[var(--muted)] text-sm py-2">No events match the current filters.</div>`
      : `<div class="text-[var(--muted)] text-sm py-2">No bulk activity recorded yet. New rips, purchases, trades, and sales involving bulk will show up here.</div>`;
  }
  let running = 0;
  const rows = events.map(ev => {
    running += ev.delta;
    const sign = ev.delta > 0 ? "+" : (ev.delta < 0 ? "−" : "·");
    const color = ev.delta > 0 ? "text-emerald-300" : (ev.delta < 0 ? "text-rose-300" : "text-[var(--muted)]");
    const deltaLabel = ev.delta === 0 ? `${sign}` : `${sign}${fmtN(Math.abs(ev.delta))}`;
    return `
      <tr class="border-b border-[var(--border)]/40">
        <td class="py-1 pr-3 text-xs text-[var(--muted)] whitespace-nowrap">${escapeHtml(fmtDate(ev.date))}</td>
        <td class="py-1 pr-3 text-xs whitespace-nowrap"><span class="pill pill-${srcPillColor(ev.source)}">${ev.source}</span></td>
        <td class="py-1 pr-3">${idChip(ev.sourceId)}</td>
        <td class="py-1 pr-3 text-right stat-num font-semibold ${color} whitespace-nowrap">${deltaLabel}</td>
        <td class="py-1 pr-3 text-right text-xs text-[var(--muted)] stat-num whitespace-nowrap">${fmtN(running)}</td>
        <td class="py-1 text-xs text-[var(--muted)]">${escapeHtml(ev.note)}</td>
      </tr>`;
  }).join("");
  return `
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs text-[var(--muted)] border-b border-[var(--border)]">
            <th class="text-left py-1 pr-3">Date</th>
            <th class="text-left py-1 pr-3">Source</th>
            <th class="text-left py-1 pr-3">ID</th>
            <th class="text-right py-1 pr-3">Δ Qty</th>
            <th class="text-right py-1 pr-3">Running</th>
            <th class="text-left py-1">Note</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      ${!currentFilterActive && running !== poolQty ? `<div class="text-xs text-amber-300 mt-2">Note: running total (<b>${fmtN(running)}</b>) differs from current pool qty (<b>${fmtN(poolQty)}</b>) — the gap is from manual ✎ adjustments and pre-Phase-6 rips (their bulk pulls weren't persisted).</div>` : ""}
    </div>`;
}

function applyBulkFilters(events: BulkActivityEvent[]): { filtered: BulkActivityEvent[]; active: boolean } {
  const f = _bulkFilters;
  let out = events;
  const active = f.sources.size > 0 || !!f.from || !!f.to;
  if (f.sources.size > 0) out = out.filter(e => f.sources.has(e.source));
  if (f.from)             out = out.filter(e => String(e.date || "") >= f.from);
  if (f.to)               out = out.filter(e => String(e.date || "") <= f.to);
  return { filtered: out, active };
}

export function renderBulkActivityFragment(poolId: string): void {
  const pool = DB.table("cards").find(r => r["Card Lot ID"] === poolId);
  if (!pool) return;
  const qty = Number(pool["Qty"]) || 0;
  const all = collectBulkActivity(poolId);
  const { filtered, active } = applyBulkFilters(all);
  const host = document.getElementById("bulkActivityFragment");
  if (host) host.innerHTML = bulkActivityTableHtml(filtered, qty, active);
  document.querySelectorAll<HTMLElement>("[data-bulk-src]").forEach(btn => {
    const s = btn.getAttribute("data-bulk-src") || "";
    btn.setAttribute("data-active", String(_bulkFilters.sources.has(s)));
  });
  const counts = document.getElementById("bulkFilterCounts");
  if (counts) {
    counts.textContent = active
      ? `Showing ${filtered.length} of ${all.length} events`
      : `${all.length} event${all.length === 1 ? "" : "s"}`;
  }
}

export function bulkHistToggleSrc(s: string): void {
  if (_bulkFilters.sources.has(s)) _bulkFilters.sources.delete(s);
  else _bulkFilters.sources.add(s);
  renderBulkActivityFragment(_bulkFilters.poolId);
}
export function bulkHistSetFrom(v: string): void {
  _bulkFilters.from = v || "";
  renderBulkActivityFragment(_bulkFilters.poolId);
}
export function bulkHistSetTo(v: string): void {
  _bulkFilters.to = v || "";
  renderBulkActivityFragment(_bulkFilters.poolId);
}
export function bulkHistClear(): void {
  _bulkFilters.sources.clear();
  _bulkFilters.from = "";
  _bulkFilters.to = "";
  const fromInp = document.getElementById("bulkFiltFrom") as HTMLInputElement | null;
  const toInp   = document.getElementById("bulkFiltTo")   as HTMLInputElement | null;
  if (fromInp) fromInp.value = "";
  if (toInp)   toInp.value   = "";
  renderBulkActivityFragment(_bulkFilters.poolId);
}
(window as any).bulkHistToggleSrc = bulkHistToggleSrc;
(window as any).bulkHistSetFrom   = bulkHistSetFrom;
(window as any).bulkHistSetTo     = bulkHistSetTo;
(window as any).bulkHistClear     = bulkHistClear;

function renderBulkPool(r: Row): string {
  const id = r["Card Lot ID"];
  const klass = poolIdToClass(id) || "Bulk";
  const qty = Number(r["Qty"]) || 0;
  const unit = bulkUnitValueFor(klass);
  const totalBasis = +(qty * unit).toFixed(2);

  if (_bulkFilters.poolId !== id) {
    _bulkFilters = { poolId: id, sources: new Set(), from: "", to: "" };
  }

  const summary = `
    <div class="font-semibold text-base">${escapeHtml(klass)} Pool</div>
    <div class="text-xs text-[var(--muted)]">Auto-managed singleton lot · per-card value from Configs → Defaults</div>
    <div class="text-xs text-[var(--muted)] mt-1">Qty <b>${fmtN(qty)}</b> @ <b>${fmt$(unit)}</b>/card = Total Basis <b>${fmt$(totalBasis)}</b></div>`;

  const allEvents = collectBulkActivity(id);
  const { filtered, active } = applyBulkFilters(allEvents);

  const filterBar = `
    <div class="flex flex-wrap gap-2 mb-3 items-center text-xs">
      <span class="text-[var(--muted)]">Filter by source:</span>
      ${ALL_BULK_SOURCES.map(s => `
        <button class="btn btn-ghost !py-0.5 !px-2 text-xs" data-bulk-src="${s}" data-active="${_bulkFilters.sources.has(s)}"
                onclick="bulkHistToggleSrc('${s}')">
          <span class="pill pill-${srcPillColor(s)}">${s}</span>
        </button>`).join("")}
      <span class="text-[var(--muted)] ml-2">Date:</span>
      <input id="bulkFiltFrom" class="input !py-0.5 !px-2 text-xs" type="date" value="${escapeHtml(_bulkFilters.from)}"
             oninput="bulkHistSetFrom(this.value)" title="From">
      <input id="bulkFiltTo"   class="input !py-0.5 !px-2 text-xs" type="date" value="${escapeHtml(_bulkFilters.to)}"
             oninput="bulkHistSetTo(this.value)" title="To">
      <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="bulkHistClear()">Clear</button>
      <span id="bulkFilterCounts" class="ml-auto text-[var(--muted)]">${active ? `Showing ${filtered.length} of ${allEvents.length} events` : `${allEvents.length} event${allEvents.length === 1 ? "" : "s"}`}</span>
    </div>`;

  return `
    ${section("Summary", summary)}
    ${section("Activity", `${filterBar}<div id="bulkActivityFragment">${bulkActivityTableHtml(filtered, qty, active)}</div>`)}`;
}

function renderLotHistory(ev: ResolvedEvent): string {
  switch (ev.kind) {
    case "sealed":     return renderSealed(ev.row);
    case "card":       return renderCard(ev.row);
    case "rip":        return renderRip(ev.row);
    case "sale":       return renderSale(ev.row);
    case "trade":      return renderTrade(ev.row);
    case "grading":    return renderGrading(ev.row);
    case "adjustment": return renderAdjustment(ev.row);
    case "purchase":   return renderPurchase(ev.row);
    case "bulk-pool":  return renderBulkPool(ev.row);
  }
}

/* ------------------------------------------------------------------ */
/*  Public entry points                                                 */
/* ------------------------------------------------------------------ */
export function showLotHistory(id: string): void {
  if (!id) return;
  let root = document.getElementById("pickerRoot");
  if (!root) { root = document.createElement("div"); root.id = "pickerRoot"; document.body.appendChild(root); }
  const ev = resolveLotEvent(id);
  root.innerHTML = `
    <div class="fixed inset-0 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto" style="z-index:55">
      <div class="panel w-full max-w-3xl my-6 shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <h3 class="font-semibold">History · <span class="font-mono">${escapeHtml(id)}</span></h3>
          <button class="btn btn-ghost !py-1 !px-2" id="_lhClose" title="Close">✕</button>
        </div>
        <div class="p-4">${ev ? renderLotHistory(ev) : `<div class="text-sm text-[var(--muted)] py-6 text-center">No record found for <code>${escapeHtml(id)}</code>.</div>`}</div>
        <div class="p-4 border-t border-[var(--border)] flex justify-end gap-2">
          <button class="btn btn-ghost" id="_lhCloseBtn">Close</button>
        </div>
      </div>
    </div>`;
  document.getElementById("_lhClose")?.addEventListener("click", closeLotHistory);
  document.getElementById("_lhCloseBtn")?.addEventListener("click", closeLotHistory);
  // Sealed-only side effect: mount Chart.js into the market-price chart (TCGCSV).
  void wireSealedHistoryChart();
  // Both cards + sealed: mount Chart.js into the per-lot FMV-history chart.
  void wireLotFmvHistoryCharts();
}

export function closeLotHistory(): void {
  const root = document.getElementById("pickerRoot");
  if (root) root.innerHTML = "";
}
// SYNC_MARKER_2026_05_17_T2
